// src/app/pages/procedimientos/audioTrigger/trigger-factory.service.ts

import { Injectable } from '@angular/core';
import { AudioTriggerService } from './audio-trigger.service';
import { SerialTriggerService } from './serial-trigger.service';
import { KeyboardTriggerService } from './keyboard-trigger.service';

export type TriggerMode = 'audio' | 'serial' | 'keyboard' | 'auto';

/**
 * Interface unificada para Audio, Serial y Keyboard Trigger
 */
export interface IUnifiedTriggerService {
  mode: TriggerMode;
  onSenalDetectada: any; // EventEmitter<void>
  iniciar(config?: any): Promise<boolean>;
  detener(): void | Promise<void>;
  ajustarTiempoEspera(ms: number): void;
  ajustarSensibilidad?(valor: number): void; // Solo para audio
  isActive(): boolean;
}

/**
 * Factory Service para crear el trigger apropiado
 * ✅ NUEVO: Soporta Keyboard Trigger para presentadores inalámbricos
 */
@Injectable({
  providedIn: 'root',
})
export class TriggerFactoryService {
  constructor(
    private audioTriggerService: AudioTriggerService,
    private serialTriggerService: SerialTriggerService,
    private keyboardTriggerService: KeyboardTriggerService,
  ) {}

  /**
   * ⭐ NUEVO: Obtiene el servicio AudioTrigger original
   * (para compatibilidad con código existente)
   */
  getAudioTriggerService(): AudioTriggerService {
    return this.audioTriggerService;
  }

  /**
   * ⭐ NUEVO: Obtiene el servicio KeyboardTrigger
   */
  getKeyboardTriggerService(): KeyboardTriggerService {
    return this.keyboardTriggerService;
  }

  /**
   * ⭐ NUEVO: Detecta TODOS los triggers disponibles y los retorna
   * SIEMPRE incluye Keyboard Trigger
   * PRIORIDAD: FT232R (Serial) > PCM2902 (Audio) + Keyboard (siempre)
   */
  async detectarYCrearTodosTriggers(deviceId?: string): Promise<IUnifiedTriggerService[]> {
    console.log('🔍 Detectando TODOS los triggers disponibles...');

    const triggers: IUnifiedTriggerService[] = [];

    // 1️⃣ Intentar crear Serial Trigger (FT232R)
    const serialTrigger = await this.intentarCrearSerialTrigger();
    if (serialTrigger) {
      triggers.push(serialTrigger);
    }

    // 2️⃣ Intentar crear Audio Trigger (PCM2902)
    const audioTrigger = this.intentarCrearAudioTrigger(deviceId);
    if (audioTrigger) {
      triggers.push(audioTrigger);
    }

    // 3️⃣ SIEMPRE crear Keyboard Trigger (presentadores)
    const keyboardTrigger = this.crearTriggerKeyboard();
    triggers.push(keyboardTrigger);

    console.log(`✅ Triggers detectados: ${triggers.length}`);
    triggers.forEach((t) => console.log(`   - ${t.mode.toUpperCase()}`));

    return triggers;
  }

  /**
   * Detecta qué tipo de señal usa el EVIS X1 y retorna el servicio apropiado
   * PRIORIDAD: FT232R (Serial) > PCM2902 (Audio)
   */
  async detectarYCrearTrigger(deviceId?: string): Promise<IUnifiedTriggerService | null> {
    console.log('🔍 Detectando tipo de señal del EVIS X1...');

    // 1️⃣ PRIMERO: Verificar si Web Serial API está disponible
    if (!('serial' in navigator)) {
      console.log('⚠️ Web Serial API no disponible en este navegador');
      console.log('💡 Usando modo Audio (fallback)');
      return this.crearTriggerAudio(deviceId);
    }

    console.log('✅ Web Serial API disponible');
    console.log('🔍 Buscando FT232R (Vendor: 0x0403, Product: 0x6001)...');

    try {
      // 2️⃣ PASO 1: Verificar puertos YA autorizados
      let ports = await (navigator.serial as any).getPorts();
      console.log(`📊 Puertos autorizados previamente: ${ports.length}`);

      // Buscar FT232R en puertos autorizados
      for (const port of ports) {
        const info = port.getInfo();
        console.log(
          `   - Puerto autorizado: Vendor=0x${info.usbVendorId?.toString(16)}, Product=0x${info.usbProductId?.toString(
            16,
          )}`,
        );

        if (info.usbVendorId === 0x0403 && info.usbProductId === 0x6001) {
          console.log('✅✅ FT232R encontrado en puertos autorizados');
          console.log('   → Modo: SERIAL (Pulsos digitales)');

          return {
            mode: 'serial',
            onSenalDetectada: this.serialTriggerService.onPulsoDetectado,
            iniciar: (config) => this.serialTriggerService.iniciar(config),
            detener: () => this.serialTriggerService.detener(),
            ajustarTiempoEspera: (ms) => this.serialTriggerService.ajustarTiempoEspera(ms),
            isActive: () => this.serialTriggerService.isActive(),
          };
        }
      }

      // 3️⃣ PASO 2: Si NO hay FT232R autorizado, PEDIR permiso al usuario
      console.log('⚠️ FT232R no está en puertos autorizados');
      console.log('🔔 Solicitando permiso al usuario para acceder a FT232R...');

      try {
        // requestPort() muestra un popup para seleccionar el puerto
        const selectedPort = await (navigator.serial as any).requestPort({
          filters: [
            { usbVendorId: 0x0403, usbProductId: 0x6001 }, // Solo FT232R
          ],
        });

        if (selectedPort) {
          const info = selectedPort.getInfo();
          console.log(
            `✅ Usuario autorizó puerto: Vendor=0x${info.usbVendorId?.toString(
              16,
            )}, Product=0x${info.usbProductId?.toString(16)}`,
          );

          if (info.usbVendorId === 0x0403 && info.usbProductId === 0x6001) {
            console.log('✅✅ FT232R seleccionado y autorizado');
            console.log('   → Modo: SERIAL (Pulsos digitales)');

            return {
              mode: 'serial',
              onSenalDetectada: this.serialTriggerService.onPulsoDetectado,
              iniciar: (config) => this.serialTriggerService.iniciar(config),
              detener: () => this.serialTriggerService.detener(),
              ajustarTiempoEspera: (ms) => this.serialTriggerService.ajustarTiempoEspera(ms),
              isActive: () => this.serialTriggerService.isActive(),
            };
          }
        }
      } catch (requestError: any) {
        if (requestError.name === 'NotFoundError') {
          console.log('⚠️ Usuario no encontró/seleccionó el FT232R en el popup');
        } else if (requestError.name === 'SecurityError' || requestError.name === 'AbortError') {
          console.log('⚠️ Usuario canceló la selección del puerto');
        } else {
          console.log('⚠️ Error al solicitar puerto:', requestError);
        }
      }

      console.log('💡 FT232R no disponible, usando modo Audio (fallback)');
    } catch (error) {
      console.error('❌ Error verificando puertos seriales:', error);
      console.log('💡 Fallback a modo Audio');
    }

    // 4️⃣ FALLBACK: Si NO hay FT232R, usar Audio (PCM2902)
    return this.crearTriggerAudio(deviceId);
  }

  /**
   * ⭐ NUEVO: Intenta crear Serial Trigger sin mostrar popup
   */
  private async intentarCrearSerialTrigger(): Promise<IUnifiedTriggerService | null> {
    if (!('serial' in navigator)) {
      return null;
    }

    try {
      const ports = await (navigator.serial as any).getPorts();

      for (const port of ports) {
        const info = port.getInfo();

        if (info.usbVendorId === 0x0403 && info.usbProductId === 0x6001) {
          console.log('✅ FT232R detectado (autorizado previamente)');

          return {
            mode: 'serial',
            onSenalDetectada: this.serialTriggerService.onPulsoDetectado,
            iniciar: (config) => this.serialTriggerService.iniciar(config),
            detener: () => this.serialTriggerService.detener(),
            ajustarTiempoEspera: (ms) => this.serialTriggerService.ajustarTiempoEspera(ms),
            isActive: () => this.serialTriggerService.isActive(),
          };
        }
      }
    } catch (error) {
      console.log('⚠️ Error verificando Serial Trigger:', error);
    }

    return null;
  }

  /**
   * ⭐ NUEVO: Intenta crear Audio Trigger si hay dispositivo
   */
  private intentarCrearAudioTrigger(deviceId?: string): IUnifiedTriggerService | null {
    if (deviceId) {
      console.log('✅ PCM2902 detectado');
      this.audioTriggerService.setDispositivo(deviceId);

      return {
        mode: 'audio',
        onSenalDetectada: this.audioTriggerService.onSenalDetectada,
        iniciar: (config) => this.audioTriggerService.iniciar(config),
        detener: () => this.audioTriggerService.detener(),
        ajustarTiempoEspera: (ms) => this.audioTriggerService.ajustarTiempoEspera(ms),
        ajustarSensibilidad: (valor) => this.audioTriggerService.ajustarSensibilidad(valor),
        isActive: () => this.audioTriggerService.isActive(),
      };
    }

    return null;
  }

  /**
   * ⭐ NUEVO: Crea Keyboard Trigger (siempre disponible)
   */
  private crearTriggerKeyboard(): IUnifiedTriggerService {
    console.log('⌨️ Creando Keyboard Trigger (presentadores)');

    return {
      mode: 'keyboard',
      onSenalDetectada: this.keyboardTriggerService.onTeclaDetectada,
      iniciar: (config) => this.keyboardTriggerService.iniciar(config),
      detener: () => this.keyboardTriggerService.detener(),
      ajustarTiempoEspera: (ms) => this.keyboardTriggerService.ajustarTiempoEspera(ms),
      isActive: () => this.keyboardTriggerService.isActive(),
    };
  }

  /**
   * Crea trigger de audio (método auxiliar)
   */
  private crearTriggerAudio(deviceId?: string): IUnifiedTriggerService | null {
    if (deviceId) {
      console.log('✅ PCM2902 detectado (audio analógico)');
      console.log('   → Modo: AUDIO (Señal analógica)');

      this.audioTriggerService.setDispositivo(deviceId);

      return {
        mode: 'audio',
        onSenalDetectada: this.audioTriggerService.onSenalDetectada,
        iniciar: (config) => this.audioTriggerService.iniciar(config),
        detener: () => this.audioTriggerService.detener(),
        ajustarTiempoEspera: (ms) => this.audioTriggerService.ajustarTiempoEspera(ms),
        ajustarSensibilidad: (valor) => this.audioTriggerService.ajustarSensibilidad(valor),
        isActive: () => this.audioTriggerService.isActive(),
      };
    }

    console.warn('⚠️ No se detectó ningún trigger (ni FT232R ni PCM2902)');
    return null;
  }

  /**
   * Crea un trigger específico (sin auto-detección)
   */
  crearTrigger(mode: 'audio' | 'serial' | 'keyboard', deviceId?: string): IUnifiedTriggerService {
    if (mode === 'serial') {
      console.log('⚡ Creando Serial Trigger (modo manual)');
      return {
        mode: 'serial',
        onSenalDetectada: this.serialTriggerService.onPulsoDetectado,
        iniciar: (config) => this.serialTriggerService.iniciar(config),
        detener: () => this.serialTriggerService.detener(),
        ajustarTiempoEspera: (ms) => this.serialTriggerService.ajustarTiempoEspera(ms),
        isActive: () => this.serialTriggerService.isActive(),
      };
    } else if (mode === 'keyboard') {
      console.log('⌨️ Creando Keyboard Trigger (modo manual)');
      return this.crearTriggerKeyboard();
    } else {
      console.log('🎵 Creando Audio Trigger (modo manual)');
      if (deviceId) {
        this.audioTriggerService.setDispositivo(deviceId);
      }
      return {
        mode: 'audio',
        onSenalDetectada: this.audioTriggerService.onSenalDetectada,
        iniciar: (config) => this.audioTriggerService.iniciar(config),
        detener: () => this.audioTriggerService.detener(),
        ajustarTiempoEspera: (ms) => this.audioTriggerService.ajustarTiempoEspera(ms),
        ajustarSensibilidad: (valor) => this.audioTriggerService.ajustarSensibilidad(valor),
        isActive: () => this.audioTriggerService.isActive(),
      };
    }
  }
}
